import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getLocalState, setLocalState, removeLocalState } from '../../utils/localStorage';

const BASE_URL = process.env.REACT_APP_API_URL + '/decisions/';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Track if we're currently refreshing to avoid infinite loops
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Request interceptor: attach access token
apiClient.interceptors.request.use(
  (config) => {
    const token = getLocalState('access_token');
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retrying, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh or login requests
      const url = originalRequest.url || '';
      if (url.includes('api-token-auth') || url.includes('api-token-refresh')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getLocalState('refresh_token');

      if (!refreshToken) {
        isRefreshing = false;
        // No refresh token → force logout
        clearAuthStorage();
        window.location.href = '/connexion';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${BASE_URL}api-token-refresh/`, {
          refresh: refreshToken,
        });

        const newAccess = response.data.access;
        setLocalState('access_token', newAccess);

        processQueue(null, newAccess);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newAccess}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthStorage();
        window.location.href = '/connexion';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function clearAuthStorage() {
  removeLocalState('access_token');
  removeLocalState('refresh_token');
  removeLocalState('_currentUser');
  // Clear redux-persist state to prevent rehydration loop
  removeLocalState('persist:root');
}

class ApiService {
  _get(endpoint: string, params?: any) {
    return apiClient.get(endpoint, { params });
  }

  _get_paginate(fullUrl: string, params?: any) {
    return axios.get(fullUrl, {
      params,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getLocalState('access_token') || ''}`,
      },
    });
  }

  _post(endpoint: string, body?: any, config?: AxiosRequestConfig) {
    return apiClient.post(endpoint, body, config);
  }

  _put(endpoint: string, body?: any) {
    return apiClient.put(endpoint, body);
  }

  _patch(endpoint: string, body?: any) {
    return apiClient.patch(endpoint, body);
  }

  _delete(endpoint: string) {
    return apiClient.delete(endpoint);
  }
}

export { apiClient };
export default ApiService;
